import { useState } from 'react';
import { Link } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { holdableCalendar, useAvailability, useCancelMeeting, useEvents, useFollowing, useHold, useInvite, useMoveMeeting, useReleaseHold, useSlots, type HoldAttendees, type HoldResult, type WireFollowedCalendar } from '@/api/hooks';
import type { WireSlot } from '@/api/wire';
import { useToast } from '@/ui/Toast';
import { useMeOrThrow } from '@/app/MeContext';
import { Badge, Button, Kicker, Skeleton, TextInput, TextAction } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { clock, dayTitle, twinGiven } from './copy';
import { groupByDay } from './grid';
import { AttendeePicker, InviteStep, NOBODY } from './Attendees';
import { Select } from '@/ui/fields';

/**
 * The calendar peek: the mirrored week above Today, and "find a time" —
 * free slots inside the person's own bookable hours — and, with colleagues,
 * inside theirs too, read as busy and nothing more — and a hold on one:
 * their twin writes it on their own calendar at their word (ADR-0067), with
 * nobody else on it, so nobody is told anything. The people it is with are
 * told only by the person's own "Send invitations" (ADR-0093). Without a
 * superadmin's calendar consent (ADR-0090) the hold stays the platform's
 * and says so.
 */
export function CalendarOverlay({
  anchor,
  now,
  onClose,
  onWork,
  finding: findOnOpen = false,
}: {
  anchor: Date;
  now: Date;
  onClose: () => void;
  /** Pick a day and it becomes the day you are working on. */
  onWork?: ((day: Date) => void) | undefined;
  /** Opened from "Find a time": the free slots are asked for at once. */
  finding?: boolean;
}) {
  const me = useMeOrThrow();
  const events = useEvents('week', anchor, me.department);
  const [duration, setDuration] = useState(30);
  // Once, or the same time each week for so many weeks (0153).
  const [weeks, setWeeks] = useState(1);
  const [finding, setFinding] = useState(findOnOpen);
  const [attendees, setAttendees] = useState<HoldAttendees>(NOBODY);
  const slots = useSlots(anchor, duration, finding, attendees.humanIds, attendees.contactIds, weeks > 1 ? weeks : null);
  const following = useFollowing(anchor);
  const availability = useAvailability();
  const release = useReleaseHold();
  const toast = useToast();
  const [chosen, setChosen] = useState<WireSlot | null>(null);
  const holds = new Map((events.data?.events ?? []).filter((e) => e.source === 'platform' && e.status === 'tentative').map((e) => [e.id, e]));
  // A meeting the platform placed whose invitations went out: moved or
  // cancelled here, and Google tells its guests from the person (ADR-0121).
  const invited = new Map(
    (events.data?.events ?? [])
      .filter((e) => e.source === 'platform' && e.status === 'confirmed' && e.externalId && new Date(e.startsAt).getTime() > now.getTime())
      .map((e) => [e.id, e]),
  );
  // What is on the calendars you follow, by day: shown as theirs, never yours.
  const followedOn = (day: Date) =>
    (following.data?.events ?? []).filter((f) => {
      const start = new Date(f.startsAt);
      return start.getFullYear() === day.getFullYear() && start.getMonth() === day.getMonth() && start.getDate() === day.getDate();
    });
  const withOthers = (id: string) => (holds.get(id)?.attendees ?? []).some((a) => a.humanId !== me.humanId);

  return (
    <section aria-label="Calendar" className="flex flex-col gap-5 rounded-brand border border-strong bg-raised p-5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Kicker>Your calendar · this week</Kicker>
          <span className="text-panel font-semibold">Mirrored from Google, under your own key</span>
          <span className="text-dense text-secondary">A look ahead. Pick a day and it becomes the day you are working on.</span>
        </div>
        <Button icon="close" variant="quiet" onClick={onClose}>Close</Button>
      </div>

      {events.isPending && <Skeleton rows={4} />}
      {events.isError && <Refusal compact failure={failureOf(events.error)} />}
      {events.data && (
        <div className="grid gap-3 md:grid-cols-7">
          {groupByDay(
            events.data.events.map((e) => ({
              kind: 'calendar_event' as const,
              sourceId: e.id,
              at: e.startsAt,
              until: e.endsAt,
              durationMinutes: null,
              rank: null,
              pinned: false,
              label: e.title,
              placedBy: 'candidate' as const,
              reason: null,
            })),
            new Date(events.data.range.from),
            new Date(events.data.range.to),
          ).map(({ day, items }) => (
            <div key={day.toISOString()} className="flex flex-col gap-2 border border-line bg-page p-3">
              <span className="flex items-center justify-between gap-2">
                <span className="text-dense font-semibold">{dayTitle(day, now)}</span>
                {onWork && (
                  <button
                    type="button"
                    onClick={() => onWork(day)}
                    className="min-h-tap text-micro uppercase tracking-label text-accent underline underline-offset-[3px] hover:text-ink"
                  >
                    Work this day
                  </button>
                )}
              </span>
              {items.length === 0 && followedOn(day).length === 0 && (
                <span className="text-dense text-meta">{following.isSuccess && !following.data?.consent ? 'Not read from Google' : 'Nothing mirrored'}</span>
              )}
              {items.map((i) => (
                <span key={i.sourceId} className="flex flex-wrap items-center gap-1 text-dense" data-testid={holds.has(i.sourceId!) ? 'hold' : undefined}>
                  <span className="font-mono text-meta">{clock(i.at)}</span> {i.label ?? 'Busy'}
                  {invited.has(i.sourceId!) && <MeetingControls meeting={invited.get(i.sourceId!)!} />}
                  {holds.has(i.sourceId!) && (
                    <>
                      <Badge tone="warning">{holds.get(i.sourceId!)?.externalId ? 'HOLD · NOBODY TOLD' : 'HOLD · NOT ON GOOGLE YET'}</Badge>
                      {withOthers(i.sourceId!) && <SendInvitations eventId={i.sourceId!} />}
                      <TextAction
                       
                        className="text-label uppercase tracking-[0.08em] text-secondary underline"
                        onClick={() => release.mutate(i.sourceId!, { onSuccess: () => toast({ text: 'Let go. The time is free again.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}
                      >
                        Release
                      </TextAction>
                    </>
                  )}
                </span>
              ))}
              {followedOn(day).map((f) => (
                <span key={f.id} className="flex flex-wrap items-center gap-1 text-dense text-meta" data-testid="followed">
                  <span className="font-mono">{f.allDay ? 'all day' : clock(f.startsAt)}</span>
                  {f.busyOnly ? 'Busy' : (f.title ?? 'No title')}
                  <span className="text-label">· {f.calendarName ?? 'a calendar you follow'}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      <FollowedCalendars calendars={following.data?.calendars ?? []} consent={following.data?.consent ?? false} loaded={following.isSuccess} />

      <div className="flex flex-col gap-3 border-t border-line pt-4">
        <Kicker>Find a time</Kicker>
        <AttendeePicker
          value={attendees}
          onChange={(next) => {
            setAttendees(next);
            setChosen(null);
          }}
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-h-tap items-center gap-2 text-dense text-secondary">
            A meeting of
            <Select
              
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              aria-label="Meeting length"
            >
              {[15, 30, 45, 60, 90].map((m) => (
                <option key={m} value={m}>
                  {m} minutes
                </option>
              ))}
            </Select>
          </label>
          <label className="flex min-h-tap items-center gap-2 text-dense text-secondary">
            <Select
              value={weeks}
              onChange={(e) => {
                setWeeks(Number(e.target.value));
                setChosen(null);
              }}
              aria-label="How often"
            >
              <option value={1}>once</option>
              {[4, 6, 8, 12].map((w) => (
                <option key={w} value={w}>
                  every week for {w} weeks
                </option>
              ))}
            </Select>
          </label>
          <Button size="row" variant="secondary" onClick={() => setFinding(true)}>
            Show me when
          </Button>
        </div>
        {finding && slots.isPending && <span className="text-dense text-meta">Looking…</span>}
        {finding && slots.isError && (
          <Refusal compact failure={failureOf(slots.error)} />
        )}
        {finding && slots.data && slots.data.slots.length === 0 && (
          <p className="m-0 max-w-[60ch] text-dense text-secondary">
            No slot of that length inside your bookable hours this week{weeks > 1 ? `, and every week for ${weeks} weeks` : ''}{attendees.humanIds.length > 0 ? ' when those colleagues are free too' : ''}.
            {availability.data && availability.data.rules.length === 0 ? (
              <>
                {' '}
                You have not said when you are willing to be booked — a free calendar is not that.{' '}
                <Link to="/settings/availability">Set your hours</Link>.
              </>
            ) : null}
          </p>
        )}
        {finding && slots.data && (slots.data.unseen ?? []).length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-dense text-secondary" aria-label="Not seen" data-testid="unseen">
            {(slots.data.unseen ?? []).map((u) => (
              <li key={u.id}>
                <strong className="text-ink">{u.name}</strong> is not counted: {u.reason}
              </li>
            ))}
          </ul>
        )}
        {finding && slots.data && slots.data.slots.length > 0 && (
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label="Free slots">
            {slots.data.slots.slice(0, 24).map((s) => (
              <li key={s.from}>
                <button
                  type="button"
                  aria-pressed={chosen?.from === s.from}
                  onClick={() => setChosen(s)}
                  className={`rounded-brand border px-3 py-2 text-dense ${chosen?.from === s.from ? 'border-accent text-accent' : 'border-line'}`}
                >
                  {dayTitle(new Date(s.from), now).split(' · ')[0]} {clock(s.from)}–{clock(s.to)}
                </button>
              </li>
            ))}
          </ul>
        )}
        {finding && weeks > 1 && slots.data && slots.data.slots.length > 0 && (
          <span className="text-dense text-secondary">Each of these is free at the same time every week for {weeks} weeks{attendees.humanIds.length > 0 ? ', for those colleagues too' : ''}.</span>
        )}
        {chosen && <HoldForm key={`${chosen.from}:${weeks}`} slot={chosen} attendees={attendees} weeks={weeks} onDone={() => setChosen(null)} />}
      </div>
    </section>
  );
}

function HoldForm({ slot, attendees, weeks = 1, onDone }: { slot: WireSlot; attendees: HoldAttendees; weeks?: number; onDone: () => void }) {
  const hold = useHold();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [calendar, setCalendar] = useState('');
  const [held, setHeld] = useState<HoldResult | null>(null);
  // Invitations go from the person's own calendar, so with people on the
  // hold a shared calendar is not offered.
  const inviting = attendees.humanIds.length + attendees.contactIds.length > 0;
  const shared = (useFollowing(new Date(slot.from), !inviting).data?.calendars ?? []).filter(holdableCalendar);
  if (held) return <InviteStep held={held} onDone={onDone} />;
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="hold-form">
      <label className="flex min-w-[240px] flex-1 flex-col gap-1">
        <span className="text-label uppercase tracking-label text-meta">What it is for</span>
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} aria-label="What it is for" placeholder="Call ICA about the listing" />
      </label>
      {!inviting && shared.length > 0 && (
        <label className="flex flex-col gap-1">
          <span className="text-label uppercase tracking-label text-meta">On</span>
          <Select
            aria-label="On which calendar"
            value={calendar}
            onChange={(e) => setCalendar(e.target.value)}
            options={[{ value: '', label: 'Your calendar' }, ...shared.map((c) => ({ value: c.id, label: c.name ?? 'A shared calendar' }))]}
          />
        </label>
      )}
      <Button
        size="row"
        variant="primary"
        disabled={hold.isPending || title.trim().length < 2}
        onClick={() =>
          hold.mutate(
            { from: slot.from, to: slot.to, title: title.trim(), attendees, ...(calendar && !inviting ? { calendar } : {}), ...(weeks > 1 ? { repeatWeeks: weeks } : {}) },
            {
              onSuccess: (r) => {
                if (r.placed) {
                  // A series: each week is its own hold, and its invitations go from it on the week it is in.
                  const skipped = r.skipped ?? [];
                  toast({
                    text:
                      `Held ${clock(slot.from)}–${clock(slot.to)} for ${r.placed.length} week${r.placed.length === 1 ? '' : 's'}.` +
                      (skipped.length ? ` ${skipped.length} week${skipped.length === 1 ? ' was' : 's were'} no longer free and not held.` : '') +
                      (inviting ? ' Nobody is told until you send each week’s invitations.' : ' Nobody else is on it.'),
                  });
                  onDone();
                  return;
                }
                if ((r.attendees ?? []).length > 0) {
                  setHeld(r);
                  return;
                }
                toast({ text: `${twinGiven(r.heldBy)}, your twin, is holding ${clock(slot.from)}–${clock(slot.to)} for you. Nobody else is on it.` });
                onDone();
              },
            },
          )
        }
      >
        {weeks > 1 ? `Hold it every week for ${weeks} weeks` : 'Hold it'}
      </Button>
      {hold.isError && <Refusal compact failure={failureOf(hold.error)} />}
    </div>
  );
}

/** A hold already on the week with people on it: the press that tells them. */
/**
 * Move or cancel a meeting whose invitations went out. Each is one press,
 * the person's, and Google tells every guest from them — so the press says
 * so before it is made.
 */
export function MeetingControls({ meeting }: { meeting: { id: string; startsAt: string; endsAt: string } }) {
  const move = useMoveMeeting();
  const cancel = useCancelMeeting();
  const toast = useToast();
  const [moving, setMoving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const minutes = (new Date(meeting.endsAt).getTime() - new Date(meeting.startsAt).getTime()) / 60_000;
  const local = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [start, setStart] = useState(local(meeting.startsAt));
  const onError = (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' });
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="meeting-controls">
      <Badge tone="accent">INVITED</Badge>
      {moving ? (
        <>
          <input
            type="datetime-local"
            aria-label="New start"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="min-h-tap border border-line bg-page px-1 text-dense"
          />
          <TextAction
           
            className="text-label uppercase tracking-[0.08em] text-accent underline"
            disabled={move.isPending}
            onClick={() => {
              const from = new Date(start);
              const to = new Date(from.getTime() + minutes * 60_000);
              move.mutate(
                { eventId: meeting.id, from: from.toISOString(), to: to.toISOString() },
                { onSuccess: () => { setMoving(false); toast({ text: 'Moved. Google told everyone invited, from you.' }); }, onError },
              );
            }}
          >
            Move it and tell them
          </TextAction>
          <TextAction className="text-label uppercase tracking-[0.08em] text-secondary underline" onClick={() => setMoving(false)}>
            Keep it
          </TextAction>
        </>
      ) : (
        <TextAction className="text-label uppercase tracking-[0.08em] text-secondary underline" onClick={() => setMoving(true)}>
          Move
        </TextAction>
      )}
      {confirming ? (
        <>
          <TextAction
           
            className="text-label uppercase tracking-[0.08em] text-danger underline"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(meeting.id, { onSuccess: () => toast({ text: 'Cancelled. Google told everyone invited, from you.' }), onError })}
          >
            Cancel it and tell them
          </TextAction>
          <TextAction className="text-label uppercase tracking-[0.08em] text-secondary underline" onClick={() => setConfirming(false)}>
            Keep it
          </TextAction>
        </>
      ) : (
        <TextAction className="text-label uppercase tracking-[0.08em] text-secondary underline" onClick={() => setConfirming(true)}>
          Cancel
        </TextAction>
      )}
    </span>
  );
}

export function SendInvitations({ eventId }: { eventId: string }) {
  const invite = useInvite();
  const toast = useToast();
  return (
    <>
      <TextAction
       
        className="text-label uppercase tracking-[0.08em] text-accent underline"
        disabled={invite.isPending}
        onClick={() => invite.mutate(eventId, { onSuccess: (r) => toast({ text: `Sent. Google mailed ${r.invited === 1 ? 'the invitation' : `${r.invited} invitations`}, from you.` }) })}
      >
        Send invitations
      </TextAction>
      {invite.isError && <Refusal compact failure={failureOf(invite.error)} />}
    </>
  );
}

const READ_AS: Record<WireFollowedCalendar['read'], string> = {
  own: 'Your calendar',
  events: 'Its events are shown here',
  busy_only: 'Shared as free/busy only: busy times, no titles',
  not_shown: 'Hidden or unticked in Google, so not read',
};

/**
 * The calendars you follow in Google (ADR-0094): a colleague's, a group's, a
 * room, a holiday calendar, one someone outside shared and you added. Read
 * as you, under your calendar consent; what is on them is theirs, never your
 * meeting or your busy time.
 */
function FollowedCalendars({ calendars, consent, loaded }: { calendars: WireFollowedCalendar[]; consent: boolean; loaded: boolean }) {
  if (!loaded) return null;
  return (
    <div className="flex flex-col gap-2 border-t border-line pt-4" data-testid="followed-calendars">
      <Kicker>Calendars you follow</Kicker>
      {calendars.length === 0 ? (
        <p className="m-0 max-w-[62ch] text-dense text-secondary">
          {consent
            ? 'None read yet. The next calendar sync lists the calendars you follow in Google — a colleague’s, a room, one someone outside shared with you.'
            : 'The platform does not read your Google calendars — your own included — until a superadmin lets it act in your calendar, on your page under People. Holds you place here stay here until then.'}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0" aria-label="Calendars you follow">
          {calendars.map((c) => (
            <li key={c.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 bg-page px-3 py-2 text-dense">
              <span className="font-semibold">{c.name ?? 'Untitled calendar'}</span>
              {c.external && <Badge tone="neutral">OUTSIDE</Badge>}
              <span className="text-meta">{READ_AS[c.read]}</span>
              {c.read !== 'not_shown' && (
                <span className="text-meta" data-testid="calendar-freshness">
                  · {c.live ? 'updates as it changes in Google' : 'read every half hour'}
                </span>
              )}
              {c.source === 'shared' && (
                <span className="text-meta">· shared with you by {c.sharedByName ?? 'a colleague'} in Google, picked up without your adding it</span>
              )}
              {c.lastError && <span className="basis-full text-warning">{c.lastError}</span>}
              {!c.live && c.feedRefused && (
                <span className="basis-full text-meta" data-testid="calendar-feed-refused">
                  Not heard as it changes: {c.feedRefused}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
